<template>
  <main class="billing-page">
    <header class="billing-header">
      <div>
        <span class="billing-eyebrow">Sua assinatura</span>
        <h1>Pagamentos</h1>
        <p>Veja sua fatura, acompanhe o plano contratado e consulte pagamentos anteriores.</p>
      </div>

      <button
        class="refresh-button"
        type="button"
        :disabled="pageLoading"
        aria-label="Atualizar pagamentos"
        title="Atualizar pagamentos"
        @click="loadPaymentData"
      >
        <i class="ph ph-arrow-clockwise" :class="{ 'is-spinning': pageLoading }" aria-hidden="true"></i>
      </button>
    </header>

    <section class="billing-overview" aria-label="Resumo de pagamento">
      <article class="invoice-card" :class="invoiceCardClass" aria-labelledby="invoice-title">
        <div class="invoice-card__topline">
          <span>Fatura atual</span>
          <span v-if="hasCurrentInvoice" class="billing-status" :class="invoiceStatusClass">
            {{ invoiceStatusLabel }}
          </span>
        </div>

        <template v-if="isLoadingSummary">
          <div class="invoice-card__loading">
            <span class="skeleton skeleton--label"></span>
            <span class="skeleton skeleton--value"></span>
            <span class="skeleton skeleton--line"></span>
          </div>
        </template>

        <template v-else-if="hasCurrentInvoice">
          <h2 id="invoice-title">{{ summaryProductValue }}</h2>
          <p>{{ invoiceTypeDescription }}</p>

          <div class="invoice-card__meta">
            <span>
              <i class="ph ph-calendar-check" aria-hidden="true"></i>
              {{ formattedInvoiceReference }}
            </span>
            <span>
              <i class="ph ph-receipt" aria-hidden="true"></i>
              {{ invoiceTypeLabel }}
            </span>
          </div>

          <div v-if="isInvoicePaid" class="billing-feedback billing-feedback--success">
            <i class="ph ph-check-circle" aria-hidden="true"></i>
            <span>Esta fatura já foi paga. Obrigado por manter sua assinatura em dia.</span>
          </div>

          <div v-else-if="getSummary.payment_url" class="invoice-card__actions">
            <a :href="getSummary.payment_url" target="_blank" rel="noopener noreferrer">
              Pagar fatura
              <i class="ph ph-arrow-right" aria-hidden="true"></i>
            </a>
          </div>

          <div v-else class="billing-feedback billing-feedback--warning">
            <i class="ph ph-clock" aria-hidden="true"></i>
            <span>O link de pagamento ainda não está disponível para esta fatura.</span>
          </div>
        </template>

        <div v-else class="billing-empty billing-empty--compact">
          <span><i class="ph ph-receipt" aria-hidden="true"></i></span>
          <strong>Nenhuma fatura disponível</strong>
          <p>{{ getErrorSummary || 'Não há faturas disponíveis no momento.' }}</p>
        </div>
      </article>

      <article class="plan-card" aria-labelledby="plan-title">
        <div class="plan-card__heading">
          <div>
            <span class="billing-eyebrow">Plano contratado</span>
            <h2 id="plan-title">{{ currentPlan ? currentPlanName : 'Seu plano' }}</h2>
          </div>
          <span class="plan-card__icon"><i class="ph ph-crown-simple" aria-hidden="true"></i></span>
        </div>

        <div v-if="getLoadingCurrentPlan" class="billing-surface-state">
          Carregando plano...
        </div>

        <template v-else-if="currentPlan">
          <p>{{ currentPlanDescription }}</p>

          <div class="plan-card__price">
            <strong>{{ formatPlanPrice(currentPlan) }}</strong>
            <span>{{ currentPlanBillingCycle }}</span>
          </div>

          <ul v-if="currentPlanFeatures.length" class="plan-card__features">
            <li v-for="(feature, index) in currentPlanFeatures.slice(0, 4)" :key="index">
              <i class="ph ph-check-circle" aria-hidden="true"></i>
              <span>{{ feature }}</span>
            </li>
          </ul>

          <button type="button" class="plan-card__action" @click="togglePlanSelection">
            <span>{{ showPlanSelection ? 'Fechar opções' : 'Mudar plano' }}</span>
            <i :class="showPlanSelection ? 'ph ph-caret-up' : 'ph ph-arrow-right'" aria-hidden="true"></i>
          </button>
        </template>

        <div v-else class="billing-surface-state">
          {{ getErrorCurrentPlan || 'Nenhum plano contratado encontrado.' }}
        </div>
      </article>
    </section>

    <section v-if="showPlanSelection" class="plan-options-panel" aria-labelledby="plan-options-title">
      <div class="plan-options-panel__heading">
        <div>
          <span class="billing-eyebrow">Trocar plano</span>
          <h2 id="plan-options-title">Escolha um novo plano</h2>
          <p>Ao trocar, uma fatura de ajuste pode ser criada para finalizar a mudança.</p>
        </div>
      </div>

      <div v-if="getLoadingAvailablePlans" class="plan-options-grid">
        <div v-for="item in 2" :key="item" class="plan-option-card plan-option-card--loading">
          <span class="skeleton skeleton--label"></span>
          <span class="skeleton skeleton--line"></span>
          <span class="skeleton skeleton--button"></span>
        </div>
      </div>

      <div v-else-if="otherPlans.length" class="plan-options-grid">
        <article v-for="plan in otherPlans" :key="plan.id" class="plan-option-card">
          <div>
            <h3>{{ plan.name || plan.title || 'Plano' }}</h3>
            <p>{{ formatPlanDescription(plan) }}</p>
          </div>

          <div class="plan-option-card__footer">
            <strong>{{ formatPlanPrice(plan) }}</strong>
            <span>{{ formatPlanBillingCycle(plan) }}</span>
            <button type="button" :disabled="getLoadingChangePlan" @click="changePlan(plan)">
              {{ getLoadingChangePlan && selectedPlanId === plan.id ? 'Trocando...' : 'Trocar' }}
            </button>
          </div>
        </article>
      </div>

      <div v-else class="billing-empty billing-empty--compact">
        <span><i class="ph ph-stack" aria-hidden="true"></i></span>
        <strong>Nenhum outro plano disponível</strong>
        <p>{{ getErrorAvailablePlans || 'Não há outros planos disponíveis para troca.' }}</p>
      </div>

      <div v-if="getErrorChangePlan" class="billing-feedback billing-feedback--error" role="alert">
        <i class="ph ph-warning-circle" aria-hidden="true"></i>
        <span>{{ getErrorChangePlan }}</span>
      </div>

      <div v-if="getSuccessChangePlan" class="billing-feedback billing-feedback--success">
        <i class="ph ph-check-circle" aria-hidden="true"></i>
        <span>{{ getSuccessChangePlan }}</span>
      </div>
    </section>

    <PaymentHistory />

    <section class="payment-contact-card" aria-labelledby="billing-contact-title">
      <div class="payment-contact-card__heading">
        <span><i class="ph ph-headset" aria-hidden="true"></i></span>
        <div>
          <span class="billing-eyebrow">Ajuda</span>
          <h2 id="billing-contact-title">Contato financeiro</h2>
          <p>Precisa de ajuda com fatura, plano ou regularização de acesso?</p>
        </div>
      </div>

      <div class="payment-contact-card__actions">
        <a href="mailto:contato@sanvault.com.br">
          <span><i class="ph ph-envelope-simple" aria-hidden="true"></i></span>
          <div>
            <small>E-mail</small>
            <strong>contato@sanvault.com.br</strong>
          </div>
          <i class="ph ph-arrow-right" aria-hidden="true"></i>
        </a>

        <a href="https://wa.me/5511915100084" target="_blank" rel="noopener noreferrer">
          <span><i class="ph ph-whatsapp-logo" aria-hidden="true"></i></span>
          <div>
            <small>WhatsApp</small>
            <strong>(11) 9 1510-0084</strong>
          </div>
          <i class="ph ph-arrow-right" aria-hidden="true"></i>
        </a>
      </div>
    </section>
  </main>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import PaymentHistory from "./PaymentHistory.vue";

export default {
  name: "CheckoutContent",
  components: {
    PaymentHistory,
  },
  data() {
    return {
      isLoadingSummary: true,
      refreshing: false,
      showPlanSelection: false,
      selectedPlanId: null,
    };
  },
  async mounted() {
    await this.loadPaymentData();
  },
  computed: {
    ...mapGetters([
      "getSummary",
      "getErrorSummary",
      "getCurrentPlan",
      "getAvailablePlans",
      "getLoadingCurrentPlan",
      "getLoadingAvailablePlans",
      "getLoadingChangePlan",
      "getErrorCurrentPlan",
      "getErrorAvailablePlans",
      "getErrorChangePlan",
      "getSuccessChangePlan",
    ]),
    pageLoading() {
      return this.refreshing || this.isLoadingSummary || this.getLoadingCurrentPlan || this.getLoadingAvailablePlans;
    },
    hasCurrentInvoice() {
      return !!(this.getSummary && this.getSummary.id);
    },
    isInvoicePaid() {
      return this.getSummary && this.getSummary.status === "paid";
    },
    invoiceCardClass() {
      if (this.isInvoicePaid) return "is-paid";
      if (this.getSummary && this.getSummary.status === "overdue") return "is-overdue";
      return "is-pending";
    },
    invoiceStatusLabel() {
      if (!this.getSummary || !this.getSummary.status) return "Indisponível";

      const statusLabels = {
        paid: "Paga",
        pending: "Pendente",
        overdue: "Vencida",
        canceled: "Cancelada",
      };

      return statusLabels[this.getSummary.status] || this.getSummary.status;
    },
    invoiceStatusClass() {
      if (this.isInvoicePaid) return "is-paid";
      if (this.getSummary && this.getSummary.status === "overdue") return "is-overdue";
      if (this.getSummary && this.getSummary.status === "canceled") return "is-canceled";
      return "is-pending";
    },
    formattedInvoiceValue() {
      if (!this.getSummary) return "0,00";
      return this.getSummary.value_formatted || String(this.getSummary.value || "0,00").replace(".", ",");
    },
    formattedInvoiceReference() {
      if (!this.getSummary || !this.getSummary.month || !this.getSummary.year) return "Sem referência";

      const months = [
        "Janeiro",
        "Fevereiro",
        "Março",
        "Abril",
        "Maio",
        "Junho",
        "Julho",
        "Agosto",
        "Setembro",
        "Outubro",
        "Novembro",
        "Dezembro",
      ];
      const monthIndex = Number(this.getSummary.month) - 1;
      const monthName = months[monthIndex] || String(this.getSummary.month).padStart(2, "0");

      return `${monthName}/${this.getSummary.year}`;
    },
    invoiceTypeLabel() {
      if (!this.getSummary || !this.getSummary.invoice_type) return "Fatura mensal";

      const invoiceTypeLabels = {
        monthly: "Fatura mensal",
        couple: "Fatura do casal",
        plan_change: "Troca de plano",
      };

      return invoiceTypeLabels[this.getSummary.invoice_type] || this.getSummary.invoice_type;
    },
    invoiceTypeDescription() {
      const invoiceType = this.getSummary ? this.getSummary.invoice_type : "monthly";
      const descriptions = {
        couple: "Uma única cobrança para os dois. O pagamento realizado por qualquer participante aparece nas duas contas.",
        monthly: `Referente ao aplicativo financeiro da competência ${this.formattedInvoiceReference}.`,
        plan_change: `Referente à troca de plano do aplicativo financeiro na competência ${this.formattedInvoiceReference}.`,
      };

      return descriptions[invoiceType] || `Referente ao aplicativo financeiro da competência ${this.formattedInvoiceReference}.`;
    },
    summaryProductValue() {
      return this.hasCurrentInvoice ? `R$ ${this.formattedInvoiceValue}` : "Indisponível";
    },
    currentPlan() {
      const payload = this.getCurrentPlan;
      if (!payload) return null;
      return payload.plan || payload.current_plan || payload.data || payload;
    },
    availablePlans() {
      const payload = this.getAvailablePlans;
      if (!payload) return [];
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload.data)) return payload.data;
      if (Array.isArray(payload.plans)) return payload.plans;
      return [];
    },
    otherPlans() {
      if (!this.currentPlan || !this.currentPlan.id) return this.availablePlans;
      return this.availablePlans.filter((plan) => plan.id !== this.currentPlan.id);
    },
    currentPlanName() {
      return this.currentPlan.name || this.currentPlan.title || "Plano";
    },
    currentPlanDescription() {
      return this.formatPlanDescription(this.currentPlan);
    },
    currentPlanBillingCycle() {
      return this.formatPlanBillingCycle(this.currentPlan);
    },
    currentPlanFeatures() {
      const features = this.currentPlan.features || this.currentPlan.benefits || [];
      if (!Array.isArray(features)) return [];

      return features.map((feature) => {
        if (typeof feature === "string") return feature;
        return feature.name || feature.description || "";
      }).filter(Boolean);
    },
  },
  methods: {
    ...mapActions([
      "setSummary",
      "setInvoiceHistory",
      "setCurrentPlan",
      "setAvailablePlans",
      "setChangePlan",
    ]),
    async loadPaymentData() {
      if (this.refreshing) return;
      this.refreshing = true;
      this.isLoadingSummary = true;

      try {
        await Promise.all([
          this.setSummary(),
          this.setInvoiceHistory(),
          this.setCurrentPlan(),
          this.setAvailablePlans(),
        ]);
      } finally {
        this.isLoadingSummary = false;
        this.refreshing = false;
      }
    },
    togglePlanSelection() {
      this.showPlanSelection = !this.showPlanSelection;
    },
    formatPlanDescription(plan) {
      if (!plan) return "";

      return plan.description
        || plan.short_description
        || plan.subtitle
        || plan.resume
        || plan.summary
        || "Plano pensado para organizar gastos, recorrências e indicadores financeiros do seu negócio.";
    },
    formatPlanPrice(plan) {
      if (!plan) return "Indisponível";

      if (plan.price_formatted) {
        return plan.price_formatted.indexOf("R$") === 0 ? plan.price_formatted : `R$ ${plan.price_formatted}`;
      }

      if (plan.value_formatted) {
        return plan.value_formatted.indexOf("R$") === 0 ? plan.value_formatted : `R$ ${plan.value_formatted}`;
      }

      const rawValue = plan.price || plan.value || plan.amount;
      if (rawValue === undefined || rawValue === null || rawValue === "") return "Indisponível";

      const numericValue = Number(rawValue);
      if (Number.isNaN(numericValue)) return `R$ ${String(rawValue).replace(".", ",")}`;

      return numericValue.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    formatPlanBillingCycle(plan) {
      if (!plan) return "";

      const cycle = plan.billing_cycle || plan.interval || plan.period || plan.recurrence;
      const cycleLabels = {
        month: "/mês",
        monthly: "/mês",
        mes: "/mês",
        mês: "/mês",
        year: "/ano",
        yearly: "/ano",
        ano: "/ano",
      };

      return cycleLabels[cycle] || cycle || "/mês";
    },
    async changePlan(plan) {
      if (!plan || !plan.id || this.getLoadingChangePlan) return;

      this.selectedPlanId = plan.id;
      const changed = await this.setChangePlan(plan.id);
      this.selectedPlanId = null;

      if (changed) {
        this.showPlanSelection = false;
        await Promise.all([
          this.setSummary(),
          this.setCurrentPlan(),
          this.setAvailablePlans(),
        ]);
      }
    },
  },
};
</script>

<style scoped>
.billing-page {
  --billing-accent: #06a77d;
  --billing-accent-dark: #05785d;
  --billing-danger: #c74f4a;
  --billing-ink: #171629;
  --billing-muted: #747487;
  --billing-surface: #ffffff;
  --billing-soft: #f4f5f8;
  --billing-border: #e9e9f0;
  color: var(--billing-ink);
  margin: 0 auto;
  max-width: 1480px;
  padding-bottom: 8px;
  width: 100%;
}

.billing-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
}

.billing-eyebrow {
  color: var(--billing-accent-dark);
  display: block;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  margin-bottom: 5px;
  text-transform: uppercase;
}

.billing-header h1 {
  color: var(--billing-ink);
  font-size: clamp(24px, 3vw, 31px);
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.12;
  margin: 0;
}

.billing-header p,
.plan-card p,
.plan-options-panel__heading p,
.payment-contact-card__heading p {
  color: var(--billing-muted);
  font-size: 13px;
  line-height: 1.45;
  margin: 5px 0 0;
}

.refresh-button {
  align-items: center;
  background: var(--billing-surface);
  border: 1px solid var(--billing-border);
  border-radius: 50%;
  color: var(--billing-ink);
  display: inline-flex;
  flex: 0 0 44px;
  font-size: 20px;
  height: 44px;
  justify-content: center;
  transition: border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.refresh-button:hover:not(:disabled) {
  border-color: var(--billing-accent);
  color: var(--billing-accent-dark);
  transform: translateY(-1px);
}

.refresh-button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.billing-overview {
  align-items: stretch;
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1.25fr) minmax(330px, 0.75fr);
  margin-bottom: 16px;
}

.invoice-card,
.plan-card,
.plan-options-panel,
.payment-contact-card {
  background: var(--billing-surface);
  border: 1px solid var(--billing-border);
  border-radius: 22px;
  box-shadow: 0 10px 30px rgba(23, 22, 41, 0.035);
  min-width: 0;
  padding: 24px;
}

.invoice-card {
  background:
    radial-gradient(circle at 94% 12%, rgba(255,255,255,0.15), transparent 33%),
    linear-gradient(140deg, #075b49 0%, #073d36 54%, #142b2a 100%);
  color: #fff;
  min-height: 286px;
  overflow: hidden;
  position: relative;
}

.invoice-card::after {
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 50%;
  content: "";
  height: 190px;
  position: absolute;
  right: -78px;
  top: 62px;
  width: 190px;
}

.invoice-card__topline {
  align-items: center;
  color: rgba(255,255,255,0.78);
  display: flex;
  font-size: 13px;
  font-weight: 800;
  justify-content: space-between;
  position: relative;
  z-index: 1;
}

.invoice-card h2 {
  color: #fff;
  font-size: clamp(42px, 6vw, 58px);
  font-weight: 800;
  letter-spacing: -0.05em;
  line-height: 1;
  margin: 48px 0 14px;
  overflow-wrap: anywhere;
  position: relative;
  z-index: 1;
}

.invoice-card p {
  color: rgba(255,255,255,0.74);
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
  max-width: 560px;
  position: relative;
  z-index: 1;
}

.invoice-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 22px;
  position: relative;
  z-index: 1;
}

.invoice-card__meta span {
  align-items: center;
  background: rgba(255,255,255,0.11);
  border: 1px solid rgba(255,255,255,0.13);
  border-radius: 999px;
  color: rgba(255,255,255,0.86);
  display: inline-flex;
  font-size: 12px;
  font-weight: 800;
  gap: 6px;
  min-height: 32px;
  padding: 0 11px;
}

.invoice-card__actions,
.invoice-card .billing-feedback {
  margin-top: 20px;
  position: relative;
  z-index: 1;
}

.invoice-card__actions a,
.plan-card__action,
.plan-option-card button {
  align-items: center;
  background: var(--billing-accent);
  border: 0;
  border-radius: 13px;
  color: #fff;
  display: inline-flex;
  font-size: 14px;
  font-weight: 800;
  gap: 8px;
  justify-content: center;
  min-height: 48px;
  padding: 0 17px;
  text-decoration: none;
}

.invoice-card__loading {
  display: grid;
  gap: 18px;
  margin-top: 48px;
  position: relative;
  z-index: 1;
}

.skeleton {
  animation: billing-pulse 1.4s ease-in-out infinite;
  background: rgba(255,255,255,0.17);
  border-radius: 999px;
  display: block;
}

.skeleton--label {
  height: 16px;
  width: 130px;
}

.skeleton--value {
  height: 52px;
  width: min(300px, 82%);
}

.skeleton--line {
  height: 14px;
  width: min(420px, 92%);
}

.skeleton--button {
  height: 44px;
  width: 110px;
}

.billing-status {
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  font-size: 10px;
  font-weight: 800;
  min-height: 28px;
  padding: 0 10px;
  white-space: nowrap;
}

.billing-status.is-paid {
  background: #e8f8f3;
  color: var(--billing-accent-dark);
}

.billing-status.is-pending {
  background: #fff4df;
  color: #a76807;
}

.billing-status.is-overdue,
.billing-status.is-canceled {
  background: #fff0ee;
  color: var(--billing-danger);
}

.invoice-card .billing-status {
  background: rgba(255,255,255,0.13);
  border: 1px solid rgba(255,255,255,0.14);
}

.invoice-card .billing-status.is-paid {
  color: #8ef5ce;
}

.invoice-card .billing-status.is-pending {
  color: #ffe2aa;
}

.invoice-card .billing-status.is-overdue,
.invoice-card .billing-status.is-canceled {
  color: #ffd2ca;
}

.plan-card__heading,
.plan-options-panel__heading,
.payment-contact-card__heading {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 18px;
}

.plan-card h2,
.plan-options-panel h2,
.payment-contact-card h2 {
  color: var(--billing-ink);
  font-size: 21px;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 0;
}

.plan-card__icon,
.payment-contact-card__heading > span {
  align-items: center;
  background: #e8f8f3;
  border-radius: 16px;
  color: var(--billing-accent-dark);
  display: inline-flex;
  flex: 0 0 54px;
  font-size: 25px;
  height: 54px;
  justify-content: center;
  width: 54px;
}

.plan-card__price {
  background: var(--billing-soft);
  border-radius: 16px;
  margin: 17px 0;
  padding: 16px;
}

.plan-card__price strong {
  color: var(--billing-ink);
  display: block;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.035em;
  line-height: 1;
  overflow-wrap: anywhere;
}

.plan-card__price span {
  color: var(--billing-muted);
  display: block;
  font-size: 12px;
  font-weight: 700;
  margin-top: 5px;
}

.plan-card__features {
  display: grid;
  gap: 9px;
  list-style: none;
  margin: 0 0 18px;
  padding: 0;
}

.plan-card__features li {
  align-items: flex-start;
  color: var(--billing-muted);
  display: flex;
  font-size: 13px;
  gap: 8px;
  line-height: 1.4;
}

.plan-card__features i {
  color: var(--billing-accent-dark);
  font-size: 16px;
  margin-top: 1px;
}

.plan-card__action {
  justify-content: space-between;
  width: 100%;
}

.billing-surface-state {
  background: var(--billing-soft);
  border: 1px solid var(--billing-border);
  border-radius: 15px;
  color: var(--billing-muted);
  font-size: 13px;
  padding: 14px 16px;
}

.plan-options-panel {
  margin-bottom: 16px;
}

.plan-options-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
}

.plan-option-card {
  background: var(--billing-soft);
  border: 1px solid var(--billing-border);
  border-radius: 17px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 190px;
  padding: 16px;
}

.plan-option-card h3 {
  color: var(--billing-ink);
  font-size: 16px;
  font-weight: 800;
  margin: 0 0 7px;
}

.plan-option-card p {
  color: var(--billing-muted);
  font-size: 12px;
  line-height: 1.45;
  margin: 0;
}

.plan-option-card__footer {
  align-items: center;
  display: grid;
  gap: 4px 10px;
  grid-template-columns: minmax(0, 1fr) auto;
  margin-top: 18px;
}

.plan-option-card__footer strong {
  color: var(--billing-ink);
  font-size: 18px;
  font-weight: 800;
}

.plan-option-card__footer span {
  color: var(--billing-muted);
  font-size: 11px;
  grid-column: 1;
}

.plan-option-card__footer button {
  grid-column: 2;
  grid-row: 1 / span 2;
  min-height: 40px;
  padding: 0 14px;
}

.plan-option-card__footer button:disabled {
  opacity: 0.65;
}

.plan-option-card--loading {
  justify-content: center;
}

.plan-option-card--loading .skeleton {
  background: #e8e8ee;
  margin-bottom: 12px;
}

.billing-feedback {
  align-items: center;
  border-radius: 14px;
  display: flex;
  font-size: 12px;
  font-weight: 700;
  gap: 8px;
  padding: 12px 14px;
}

.billing-feedback--success {
  background: #e8f8f3;
  border: 1px solid #c9eee2;
  color: var(--billing-accent-dark);
}

.billing-feedback--warning {
  background: #fff4df;
  border: 1px solid #ffe4ab;
  color: #8a5f05;
}

.billing-feedback--error {
  background: #fff1f0;
  border: 1px solid #ffd5d1;
  color: #9c3732;
}

.billing-empty {
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 42px 14px 30px;
  text-align: center;
}

.billing-empty--compact {
  background: var(--billing-soft);
  border-radius: 16px;
  margin-top: 16px;
  padding: 28px 14px;
}

.invoice-card .billing-empty--compact {
  background: rgba(255,255,255,0.1);
  color: #fff;
  position: relative;
  z-index: 1;
}

.billing-empty > span {
  align-items: center;
  background: #e8f8f3;
  border-radius: 50%;
  color: var(--billing-accent-dark);
  display: flex;
  font-size: 27px;
  height: 60px;
  justify-content: center;
  margin-bottom: 13px;
  width: 60px;
}

.invoice-card .billing-empty > span {
  background: rgba(255,255,255,0.12);
  color: #fff;
}

.billing-empty strong {
  color: var(--billing-ink);
  font-size: 16px;
  font-weight: 800;
}

.invoice-card .billing-empty strong,
.invoice-card .billing-empty p {
  color: #fff;
}

.billing-empty p {
  color: var(--billing-muted);
  line-height: 1.45;
  margin: 6px 0 0;
}

.payment-contact-card {
  margin-top: 16px;
}

.payment-contact-card__actions {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.payment-contact-card__actions a {
  align-items: center;
  border: 1px solid var(--billing-border);
  border-radius: 16px;
  color: var(--billing-ink);
  display: grid;
  gap: 11px;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  min-height: 70px;
  padding: 12px;
  text-decoration: none;
}

.payment-contact-card__actions a > span {
  align-items: center;
  background: #e8f8f3;
  border-radius: 13px;
  color: var(--billing-accent-dark);
  display: inline-flex;
  font-size: 20px;
  height: 42px;
  justify-content: center;
  width: 42px;
}

.payment-contact-card__actions small,
.payment-contact-card__actions strong {
  display: block;
}

.payment-contact-card__actions small {
  color: var(--billing-muted);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.payment-contact-card__actions strong {
  color: var(--billing-ink);
  font-size: 13px;
  font-weight: 800;
  margin-top: 3px;
  overflow-wrap: anywhere;
}

.payment-contact-card__actions a > i {
  color: var(--billing-accent-dark);
}

.is-spinning {
  animation: billing-spin 0.9s linear infinite;
}

@media (max-width: 991px) {
  .billing-overview {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .billing-header {
    margin-bottom: 14px;
  }

  .billing-header h1 {
    font-size: 24px;
  }

  .billing-header p {
    font-size: 12px;
    max-width: 300px;
  }

  .invoice-card,
  .plan-card,
  .plan-options-panel,
  .payment-contact-card {
    border-radius: 20px;
    padding: 18px 16px;
  }

  .invoice-card {
    min-height: 260px;
  }

  .invoice-card h2 {
    font-size: clamp(36px, 11vw, 46px);
    margin-top: 36px;
  }

  .invoice-card p {
    font-size: 12px;
  }

  .invoice-card__topline {
    font-size: 12px;
  }

  .invoice-card__actions a {
    width: 100%;
  }

  .plan-card h2,
  .plan-options-panel h2,
  .payment-contact-card h2 {
    font-size: 19px;
  }

  .plan-card__icon,
  .payment-contact-card__heading > span {
    border-radius: 14px;
    flex-basis: 46px;
    font-size: 22px;
    height: 46px;
    width: 46px;
  }

  .plan-options-grid,
  .payment-contact-card__actions {
    grid-template-columns: 1fr;
  }

  .payment-contact-card__actions a {
    min-height: 66px;
  }
}

body.dark .billing-page {
  --billing-ink: #f5f5f8;
  --billing-muted: #aaa9ba;
  --billing-surface: #29283b;
  --billing-soft: #343347;
  --billing-border: #3b394e;
}

body.dark .billing-page .plan-card__icon,
body.dark .billing-page .payment-contact-card__heading > span,
body.dark .billing-page .payment-contact-card__actions a > span,
body.dark .billing-page .billing-status.is-paid,
body.dark .billing-page .billing-feedback--success,
body.dark .billing-page .billing-empty > span {
  background: rgba(6, 180, 138, 0.14);
  border-color: rgba(102, 224, 189, 0.22);
  color: #66e0bd;
}

body.dark .billing-page .billing-status.is-pending,
body.dark .billing-page .billing-feedback--warning {
  background: rgba(242, 169, 59, 0.16);
  border-color: rgba(242, 169, 59, 0.22);
  color: #f5bd63;
}

body.dark .billing-page .billing-status.is-overdue,
body.dark .billing-page .billing-status.is-canceled,
body.dark .billing-page .billing-feedback--error {
  background: rgba(199, 79, 74, 0.17);
  border-color: rgba(255, 170, 164, 0.2);
  color: #ffaaa4;
}

body.dark .billing-page .plan-option-card--loading .skeleton {
  background: #3a394d;
}

@keyframes billing-spin {
  to { transform: rotate(360deg); }
}

@keyframes billing-pulse {
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .is-spinning,
  .skeleton,
  .refresh-button {
    animation: none;
    transition: none;
  }
}
</style>

<style>
body.dark .billing-page {
  --billing-ink: #f5f5f8;
  --billing-muted: #aaa9ba;
  --billing-surface: #29283b;
  --billing-soft: #343347;
  --billing-border: #3b394e;
}

body.dark .billing-page .plan-card__icon,
body.dark .billing-page .payment-contact-card__heading > span,
body.dark .billing-page .payment-contact-card__actions a > span,
body.dark .billing-page .billing-status.is-paid,
body.dark .billing-page .billing-feedback--success,
body.dark .billing-page .billing-empty > span {
  background: rgba(6, 180, 138, 0.14);
  border-color: rgba(102, 224, 189, 0.22);
  color: #66e0bd;
}

body.dark .billing-page .billing-status.is-pending,
body.dark .billing-page .billing-feedback--warning {
  background: rgba(242, 169, 59, 0.16);
  border-color: rgba(242, 169, 59, 0.22);
  color: #f5bd63;
}

body.dark .billing-page .billing-status.is-overdue,
body.dark .billing-page .billing-status.is-canceled,
body.dark .billing-page .billing-feedback--error {
  background: rgba(199, 79, 74, 0.17);
  border-color: rgba(255, 170, 164, 0.2);
  color: #ffaaa4;
}

body.dark .billing-page .plan-option-card--loading .skeleton {
  background: #3a394d;
}
</style>
